import { isBoolean } from 'lodash-es';
import GroupSetting from '@/pages/messages/components/groups/group-setting';
import GroupAddMembers from '@/pages/messages/components/groups/group-add-members';
import GroupRemoveMembers from '@/pages/messages/components/groups/group-remove-members';
import GroupSearchMembers from '@/pages/messages/components/groups/group-search-members';
import GroupCreateCall from '@/pages/messages/components/groups/group-create-call';
import { useSessionContext } from '@/contexts/session/context';
import { useChatContext } from '@/contexts/chat/context';
import {
  ChatGroupMemberRemoved,
  type ChatGroupMemberIdArgs,
  type ChatGroupMemberParamsArgs,
} from '@/contexts/chat/scripts/hooks/useChatGroup/types';
import { GroupPosition, GroupStatus } from '@/configs/apis/groups/interface';
import { useContactsContext } from '@/contexts/contacts/context';
import { useChatModalContext } from '@/contexts/chatModal/context';
import { getAvatar } from '@/scripts/utils/genAvatar';
import { AddMembersFrom } from '@/contexts/chatModal/useChatModal/types';
import { ChatMessagePayloadType } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import ChatHeaderMediaCall from '../../chat/chat-main/components/chat-header/components/chat-header-media-call';
import styles from './index.module.scss';
import MoreToolIcon from './images/more.svg?react';
import type { GroupChatHeaderOperationProps } from './interface';

const GroupChatHeaderOperation = ({
  chat,
  group,
  normalMemberList,
}: GroupChatHeaderOperationProps) => {
  const {
    session: { user },
  } = useSessionContext();
  const { contacts } = useContactsContext();
  const {
    chatGroup: { removeMembers, insertMembers, chatGroupState },
    mediaCallGroup,
    chatMediaCall,
  } = useChatContext();
  const { chatModal } = useChatModalContext();
  const [openSearchMembers, setOpenSearchMembers] = useState(false);
  const [openMediaCreateCall, setOpenMediaCreateCall] = useState(false);
  const { online } = useNetwork();

  const notInGroupContacts = useMemo(() => {
    const inGroupUserIds = normalMemberList?.map((item) => item.user_id);
    return contacts.filter((item) => !inGroupUserIds?.includes(item.user_id));
  }, [normalMemberList, contacts]);

  const isGroupDisband = useMemo(
    () => group.status === GroupStatus.DISBAND,
    [group.status]
  );

  const inGroup = useMemo(() => {
    return normalMemberList?.some((member) => member.user_id === user?.userId);
  }, [normalMemberList, user?.userId]);

  const groupInfo = useMemo(() => {
    return chatGroupState.getGroup(chat.chat_id);
  }, [chat.chat_id, chatGroupState]);

  const sendMediaCall = useCallback(
    async (item: { key: string }) => {
      switch (item.key) {
        case 'select-members':
          setOpenMediaCreateCall(true);
          break;
        case ChatMessagePayloadType.MEDIA_AUDIO_CALL.toString():
        case ChatMessagePayloadType.MEDIA_VIDEO_CALL.toString():
          if (!online) {
            message.error('Network error.');
            return;
          }
          mediaCallGroup({
            chat_id: chat.chat_id,
            payload_type: Number(item.key),
            callMembers: normalMemberList
              .filter((item) => item.user_id !== user?.userId)
              .map((item) => ({
                ...item,
                userId: item.user_id,
              })),
          });
          break;
      }
    },
    [online, mediaCallGroup, chat.chat_id, normalMemberList, user?.userId]
  );

  if (isGroupDisband || !inGroup) {
    return null;
  }

  return (
    <>
      <div className={styles.operationBar}>
        <ChatHeaderMediaCall
          chatMediaCall={chatMediaCall}
          isGroup
          onClick={sendMediaCall}
        ></ChatHeaderMediaCall>
        <div
          className={styles.settings}
          onClick={() => {
            chatModal.setOpenGroupSetting(true);
          }}
        >
          <MoreToolIcon />
        </div>
      </div>
      <GroupSetting
        chat={chat}
        user={user}
        normalMemberList={normalMemberList}
        open={chatModal.openGroupSetting}
        onCancel={() => chatModal.setOpenGroupSetting(false)}
        handleSearch={() => {}}
        handleShare={() => {}}
        handAddMembers={() => {
          // chatModal.setOpenGroupSetting(false);
          chatModal.setOpenAddMembers({
            open: true,
            addFrom: AddMembersFrom.GROUP_INFO,
          });
        }}
        handDeleteMembers={() => {
          // chatModal.setOpenGroupSetting(false);
          chatModal.setOpenRemoveMembers(true);
        }}
        handSearchMembers={() => {
          // chatModal.setOpenGroupSetting(false);
          setOpenSearchMembers(true);
        }}
      ></GroupSetting>

      <GroupAddMembers
        open={chatModal.openAddMembers}
        contactList={notInGroupContacts}
        onCancel={() => {
          chatModal.setOpenAddMembers({ open: false });
          if (chatModal.addFrom === AddMembersFrom.GROUP_INFO) {
            chatModal.setOpenGroupSetting(true);
          }
        }}
        onActionOk={async (selectUsers) => {
          // await groupsApis.inviteMembers(
          //   chat.chat_id,
          //   selectUsers.map((user) => user.user_id)
          // );
          const params: ChatGroupMemberParamsArgs[] = [
            {
              id: chat.chat_id,
              is_encryption: groupInfo?.is_encryption,
              args: selectUsers.map((user) => ({
                user_id: user.user_id,
                role: GroupPosition.Member,
                avatar: getAvatar(user.avatar, user.name),
                name: user.name,
                username: user.username,
                category: GroupPosition.Member,
                create_at: user.create_at,
                update_at: user.update_at,
                remark_name: user.remark_name,
                removed: ChatGroupMemberRemoved.Normal,
              })),
            },
          ];
          await insertMembers(params);
          chatModal.setOpenAddMembers({ open: false });
          if (chatModal.addFrom === AddMembersFrom.GROUP_INFO) {
            chatModal.setOpenGroupSetting(true);
          }
        }}
      ></GroupAddMembers>
      <GroupRemoveMembers
        open={chatModal.openRemoveMembers}
        normalMemberList={normalMemberList}
        user={user}
        onCancel={() => {
          chatModal.setOpenRemoveMembers(false);
          chatModal.setOpenGroupSetting(true);
        }}
        onActionOk={async (selectUserIds) => {
          const params: ChatGroupMemberIdArgs[] = [
            {
              id: chat.chat_id,
              is_encryption: groupInfo?.is_encryption,
              args: selectUserIds,
            },
          ];
          await removeMembers(params);
          chatModal.setOpenRemoveMembers(false);
          chatModal.setOpenGroupSetting(true);
        }}
      ></GroupRemoveMembers>
      <GroupSearchMembers
        chat={chat}
        user={user}
        open={openSearchMembers}
        memberList={normalMemberList}
        onCancel={(showOpenGroupSetting) => {
          setOpenSearchMembers(false);
          if (isBoolean(showOpenGroupSetting)) {
            chatModal.setOpenGroupSetting(showOpenGroupSetting);
            return;
          }
          chatModal.setOpenGroupSetting(true);
        }}
        onActionOk={() => {
          setOpenSearchMembers(false);
          chatModal.setOpenAddMembers({ open: true });
        }}
      />

      <GroupCreateCall
        open={openMediaCreateCall}
        normalMemberList={normalMemberList.filter(
          (item) => item.user_id !== user?.userId
        )}
        onCancel={() => {
          setOpenMediaCreateCall(false);
        }}
        onCall={(payload, selectUsers) => {
          setOpenMediaCreateCall(false);
          mediaCallGroup({
            chat_id: chat.chat_id,
            payload_type: payload,
            callMembers: selectUsers.map((item) => ({ ...item, userId: item.user_id })),
          });
        }}
      ></GroupCreateCall>
    </>
  );
};

export default GroupChatHeaderOperation;
